import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Post } from '../types';
import { getMockUser, getMockUserPosts } from '../data/mockData';
import { MapPin, Calendar, ThumbsUp, MessageCircle, Share2, Edit, Settings, MoreHorizontal, BarChart3, Image as ImageIcon, X } from 'lucide-react';
import StatsSection from '../components/profile/StatsSection';
import MediaGallery from '../components/profile/MediaGallery';
import ProfileSettings from '../components/profile/ProfileSettings';

const Profile = () => {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'posts' | 'about' | 'stats' | 'media'>('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  const isOwnProfile = currentUser?.id === id;

  // Mock data for stats and media
  const mockStatsData = {
    sport: 'Basketball',
    season: '2024',
    personalBests: [
      { label: 'Puntos por Juego', value: 24.5, unit: 'pts' },
      { label: 'Rebotes', value: 8.2, unit: 'reb' },
      { label: 'Asistencias', value: 6.1, unit: 'ast' },
      { label: 'Porcentaje de Tiro', value: 47.8, unit: '%' }
    ],
    currentSeason: [
      { label: 'Juegos Jugados', value: 28, change: 12 },
      { label: 'Minutos por Juego', value: 32.4, change: 5, unit: 'min' },
      { label: 'Eficiencia', value: 18.7, change: 8 },
      { label: 'Victorias', value: 22, change: 15 }
    ],
    careerStats: [
      { label: 'Total de Juegos', value: 156 },
      { label: 'Total de Puntos', value: 3420 },
      { label: 'Promedio de Carrera', value: 21.9, unit: 'pts' },
      { label: 'Logros', value: 15 }
    ],
    competitions: [
      {
        id: '1',
        event: 'Campeonato Nacional Universitario',
        date: '2024-03-15',
        position: 1,
        location: 'Madrid, España',
        category: 'Nacional'
      },
      {
        id: '2',
        event: 'Liga Regional Femenina',
        date: '2024-02-20',
        position: 2,
        location: 'Barcelona, España',
        category: 'Regional'
      },
      {
        id: '3',
        event: 'Torneo Internacional Juvenil',
        date: '2024-01-10',
        position: 3,
        location: 'París, Francia',
        category: 'Internacional'
      }
    ],
    ranking: {
      national: 2,
      international: 15,
      category: 'Sub-23 Femenino'
    }
  };

  const mockMediaData = [
    {
      id: '1',
      type: 'video' as const,
      url: 'https://example.com/video1.mp4',
      thumbnail: 'https://images.pexels.com/photos/3621104/pexels-photo-3621104.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Highlights del Campeonato Nacional',
      description: 'Los mejores momentos de nuestra victoria en el campeonato nacional universitario.',
      category: 'highlights' as const,
      tags: ['campeonato', 'victoria', 'highlights'],
      uploadDate: '2024-03-16',
      likes: 245,
      views: 1520,
      isLiked: false
    },
    {
      id: '2',
      type: 'image' as const,
      url: 'https://images.pexels.com/photos/3621105/pexels-photo-3621105.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Entrenamiento de Fuerza',
      description: 'Sesión de entrenamiento enfocada en fuerza y resistencia.',
      category: 'training' as const,
      tags: ['entrenamiento', 'fuerza', 'gimnasio'],
      uploadDate: '2024-03-10',
      likes: 89,
      views: 456,
      isLiked: true
    },
    {
      id: '3',
      type: 'video' as const,
      url: 'https://example.com/video2.mp4',
      thumbnail: 'https://images.pexels.com/photos/3621106/pexels-photo-3621106.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Preparación Pre-Juego',
      description: 'Rutina de calentamiento y preparación mental antes del gran juego.',
      category: 'behind-scenes' as const,
      tags: ['preparación', 'rutina', 'mentalidad'],
      uploadDate: '2024-03-05',
      likes: 156,
      views: 892,
      isLiked: false
    },
    {
      id: '4',
      type: 'image' as const,
      url: 'https://images.pexels.com/photos/3764011/pexels-photo-3764011.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      title: 'Momento de Victoria',
      description: 'Celebrando con el equipo después de ganar el campeonato regional.',
      category: 'competition' as const,
      tags: ['victoria', 'equipo', 'celebración'],
      uploadDate: '2024-02-21',
      likes: 312,
      views: 1890,
      isLiked: true
    }
  ];

  useEffect(() => {
    let isMounted = true;

    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        if (id) {
          const userData = await getMockUser(id);
          if (userData && isMounted) {
            setUser(userData);
            document.title = `${userData.name} - FemSportNet`;
            
            const userPosts = await getMockUserPosts(id);
            if (isMounted) {
              setPosts(userPosts);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleFollowToggle = () => {
    setIsFollowing(!isFollowing);
    if (user) {
      setUser({
        ...user,
        stats: {
          ...user.stats,
          followers: isFollowing ? user.stats.followers - 1 : user.stats.followers + 1
        }
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">User Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">The user you're looking for doesn't exist or has been removed.</p>
          <Link to="/feed" className="bg-green-500 hover:bg-green-600 text-white font-medium px-6 py-3 rounded-md transition-colors duration-200">
            Return to Feed
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      {/* Cover Photo */}
      <div className="h-64 bg-green-500 relative">
        {user.role === 'athlete' && (
          <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-full px-3 py-1 text-sm font-medium text-gray-900 dark:text-white flex items-center">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            {user.sport}
          </div>
        )}
        {user.verified && (
          <div className="absolute bottom-4 right-4 bg-blue-500 rounded-full px-3 py-1 text-sm font-medium text-white flex items-center">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Verified
          </div>
        )}
      </div>
      
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Profile Header */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow -mt-24 relative z-10 transition-colors duration-200">
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-end">
                {/* Avatar */}
                <div className="flex-shrink-0 -mt-20 mb-4 md:mb-0 md:mr-6">
                  <div className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 overflow-hidden">
                    <img 
                      src={user.avatar} 
                      alt={user.name} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                {/* User Info */}
                <div className="flex-grow">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {user.name}
                      </h1>
                      <div className="flex items-center text-gray-600 dark:text-gray-300 mt-1 space-x-4">
                        {user.location && (
                          <div className="flex items-center">
                            <MapPin size={16} className="mr-1" />
                            <span>{user.location}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <Calendar size={16} className="mr-1" />
                          <span>Joined {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 md:mt-0 flex space-x-3">
                      {isOwnProfile ? (
                        <>
                          <button className="flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200">
                            <Edit size={16} className="mr-2" />
                            Edit Profile
                          </button>
                          <button 
                            onClick={() => setShowSettings(true)}
                            className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
                          >
                            <Settings size={18} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            onClick={handleFollowToggle}
                            className={`px-4 py-2 rounded-md font-medium ${
                              isFollowing 
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600' 
                                : 'bg-green-500 text-white hover:bg-green-600'
                            } transition-colors duration-200`}
                          >
                            {isFollowing ? 'Following' : 'Follow'}
                          </button>
                          <button className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200">
                            Message
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Bio */}
                  {user.bio && (
                    <p className="mt-4 text-gray-600 dark:text-gray-300">
                      {user.bio}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Stats */}
              <div className="mt-6 grid grid-cols-3 gap-4 border-t border-gray-200 dark:border-gray-700 pt-6">
                <div className="text-center">
                  <p className="text-xl font-semibold text-gray-900 dark:text-white">{user.stats.followers}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Followers</p>
                </div>
                {user.stats.following !== undefined && (
                  <div className="text-center">
                    <p className="text-xl font-semibold text-gray-900 dark:text-white">{user.stats.following}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Following</p>
                  </div>
                )}
                {user.role === 'athlete' && user.stats.matches !== undefined && (
                  <div className="text-center">
                    <p className="text-xl font-semibold text-gray-900 dark:text-white">{user.stats.matches}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Matches</p>
                  </div>
                )}
                {user.stats.achievements !== undefined && (
                  <div className="text-center">
                    <p className="text-xl font-semibold text-gray-900 dark:text-white">{user.stats.achievements}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Achievements</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Tabs */}
            <div className="border-t border-gray-200 dark:border-gray-700">
              <nav className="flex">
                <button
                  onClick={() => setActiveTab('posts')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'posts'
                      ? 'text-green-500 border-b-2 border-green-500'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600'
                  } transition-colors duration-200`}
                >
                  Posts
                </button>
                <button
                  onClick={() => setActiveTab('about')}
                  className={`px-4 py-3 text-sm font-medium ${
                    activeTab === 'about'
                      ? 'text-green-500 border-b-2 border-green-500'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600'
                  } transition-colors duration-200`}
                >
                  About
                </button>
                {user.role === 'athlete' && (
                  <button
                    onClick={() => setActiveTab('stats')}
                    className={`flex items-center px-4 py-3 text-sm font-medium ${
                      activeTab === 'stats'
                        ? 'text-green-500 border-b-2 border-green-500'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600'
                    } transition-colors duration-200`}
                  >
                    <BarChart3 size={16} className="mr-1" />
                    Stats
                  </button>
                )}
                <button
                  onClick={() => setActiveTab('media')}
                  className={`flex items-center px-4 py-3 text-sm font-medium ${
                    activeTab === 'media'
                      ? 'text-green-500 border-b-2 border-green-500'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-b-2 hover:border-gray-300 dark:hover:border-gray-600'
                  } transition-colors duration-200`}
                >
                  <ImageIcon size={16} className="mr-1" />
                  Media
                </button>
              </nav>
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'posts' && (
              <div className="space-y-6">
                {posts.length > 0 ? (
                  posts.map((post) => (
                    <div key={post.id} className="bg-white dark:bg-gray-800 rounded-lg shadow transition-colors duration-200">
                      {/* Post Header */}
                      <div className="p-4 flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden">
                            <img 
                              src={user.avatar} 
                              alt={user.name} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {new Date(post.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <button className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors duration-200">
                          <MoreHorizontal size={20} />
                        </button>
                      </div>
                      
                      {/* Post Content */}
                      <div className="px-4 pb-2">
                        <p className="text-gray-800 dark:text-gray-200 whitespace-pre-line">
                          {post.content}
                        </p>
                      </div>
                      
                      {/* Post Images */}
                      {post.images && post.images.length > 0 && (
                        <div className="mt-2">
                          <img 
                            src={post.images[0]} 
                            alt="Post attachment" 
                            className="w-full h-auto object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Post Video */}
                      {post.video && (
                        <div className="mt-2">
                          <video 
                            src={post.video} 
                            className="w-full h-auto" 
                            controls 
                          />
                        </div>
                      )}
                      
                      {/* Post Stats */}
                      <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 mt-2">
                        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                          <span>{post.likes} likes</span>
                          <span>·</span>
                          <span>{post.comments} comments</span>
                        </div>
                      </div>
                      
                      {/* Post Actions */}
                      <div className="px-4 py-2 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                        <button className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200">
                          <ThumbsUp size={18} />
                          <span>Like</span>
                        </button>
                        <button className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200">
                          <MessageCircle size={18} />
                          <span>Comment</span>
                        </button>
                        <button className="flex items-center space-x-1 px-3 py-1 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200">
                          <Share2 size={18} />
                          <span>Share</span>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center transition-colors duration-200">
                    <p className="text-gray-600 dark:text-gray-300">No posts yet.</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'about' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors duration-200">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">About</h2>
                <div className="space-y-4">
                  {user.bio && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Bio</h3>
                      <p className="text-gray-800 dark:text-gray-200">{user.bio}</p>
                    </div>
                  )}
                  
                  {user.role === 'athlete' && user.sport && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Sport</h3>
                      <p className="text-gray-800 dark:text-gray-200">{user.sport}</p>
                    </div>
                  )}
                  
                  {user.location && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Location</h3>
                      <p className="text-gray-800 dark:text-gray-200">{user.location}</p>
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Member Since</h3>
                    <p className="text-gray-800 dark:text-gray-200">
                      {new Date(user.createdAt).toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'stats' && user.role === 'athlete' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors duration-200">
                <StatsSection data={mockStatsData} />
              </div>
            )}

            {activeTab === 'media' && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 transition-colors duration-200">
                <MediaGallery media={mockMediaData} isOwner={isOwnProfile} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      <ProfileSettings 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
      />
    </div>
  );
};

export default Profile;